import { useEffect, useState } from "react";
import Icon from "./Icon";
import Skeleton from "../Skeleton";
import { downloadQr, downloadSignPdf, printSign, qrDataUrl, SIGN_SIZES } from "./qr";
import { notifyError } from "./useBusiness";
import { menuUrl, qrMenuUrl } from "../../menu.js";
import { PRO_TRIAL_DAYS, QR_STYLE_PRO_ONLY } from "../../plans.js";
import { QR_BLACK, QR_COLORS, qrColorOf, qrStyleAvailable, qrStyleOf } from "../../qrStyle.js";

// `onStyle` guarda el logo y el color del QR (saveQrStyle de useBusiness).
const QrPanel = ({ business, onStyle }) => {

    const url = menuUrl(business.usuarioEmpresa);
    // El QR lleva ?o=qr para las estadísticas; el texto que se muestra es el link limpio.
    const target = qrMenuUrl(business.usuarioEmpresa);
    const [src, setSrc] = useState("");
    const [size, setSize] = useState("grande");
    const [making, setMaking] = useState(false);

    // Logo y color del QR (Pro). En Gratis se muestran bloqueados.
    const allowed = qrStyleAvailable(business, QR_STYLE_PRO_ONLY);
    const chosen = qrStyleOf(business, QR_STYLE_PRO_ONLY);
    const color = qrColorOf(business, chosen);
    const logo = chosen.logo ? business.logo : "";
    const style = { color, logo };

    useEffect(() => {
        let alive = true;
        qrDataUrl(target, 600, { color, logo }).then((data) => alive && setSrc(data)).catch(console.error);
        return () => { alive = false; };
    }, [target, color, logo]);

    const handlePrint = async () => {
        if (!(await printSign(business, target, size, style))) {
            notifyError("El navegador bloqueó la ventana del cartel. Permití las ventanas emergentes y probá de nuevo.");
        }
    }

    const handlePdf = async () => {
        setMaking(true);
        try {
            await downloadSignPdf(business, target, size, `cartel-${size}-${business.usuarioEmpresa}.pdf`, style);
        } catch (error) {
            console.error(error);
            notifyError("No pudimos armar el PDF. Probá de nuevo.");
        }
        setMaking(false);
    }

  return (
    <div className="lp-panel-page">
        <div className="lp-panel-head">
            <div>
                <h1>Tu código QR</h1>
                <p className="lp-panel-muted">Si cambiás productos o precios no hace falta reimprimirlo: siempre abre tu menú actualizado.</p>
            </div>
        </div>

        <div className="lp-panel-qr-layout">
            <section className="lp-panel-card lp-panel-qr-sign">
                <span className="lp-panel-qr-name">{business.nombreEmpresa}</span>
                {src ? <img src={src} alt={`Código QR que abre ${url}`} className="lp-panel-qr-img"/> : <Skeleton className="lp-panel-qr-img" radius={12}></Skeleton>}
                <span className="lp-panel-qr-cta">Escaneá para ver el menú</span>
                <span className="lp-panel-muted">{url.replace(/^https?:\/\//, "")}</span>
            </section>

            <div className="lp-panel-stack">
                <section className="lp-panel-card lp-panel-stack">
                    <div className="lp-panel-card-head">
                        <h2>Tu logo en el QR</h2>
                        {!allowed && <span className="lp-panel-badge">Pro</span>}
                    </div>
                    <p className="lp-panel-muted">Ponele tu logo en el centro y pintalo del color de tu menú. Se sigue escaneando igual, y queda así en el PNG y en el cartel.</p>

                    {allowed ?
                        <>
                            <button type="button" className="lp-panel-switch lp-panel-switch-plain" role="switch" aria-checked={chosen.logo} disabled={!business.logo} onClick={() => onStyle({ qrLogo: !chosen.logo })}>
                                <span className="lp-panel-switch-track"><span className="lp-panel-switch-knob"></span></span>
                                <span className="lp-panel-switch-text">Logo en el centro</span>
                            </button>
                            {!business.logo && <p className="lp-panel-notice">Para ponerlo, primero cargá tu logo en <a className="lp-text-link" href="#negocio">Mi negocio</a>.</p>}

                            <div className="lp-panel-qr-colors" role="radiogroup" aria-label="Color del QR">
                                {Object.entries(QR_COLORS).map(([key, label]) => (
                                    <button key={key} type="button" role="radio" aria-checked={chosen.color === key} className={chosen.color === key ? "lp-panel-chip lp-panel-chip-on" : "lp-panel-chip"} onClick={() => onStyle({ qrColor: key })}>
                                        <span className="lp-panel-qr-dot" style={{ background: key === "marca" ? qrColorOf(business, { color: "marca" }) : QR_BLACK }} aria-hidden="true"></span>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </> :
                        <a className="lp-btn lp-btn-primary lp-panel-left" href="#cuenta">Probar Pro gratis {PRO_TRIAL_DAYS} días</a>
                    }
                </section>

                <section className="lp-panel-card lp-panel-stack">
                    <h2>Descargar e imprimir</h2>
                    <p className="lp-panel-muted">El PNG es solo el QR, para redes, flyers o una imprenta. El cartel lleva tu logo, tu nombre y el QR, y lo podés bajar en PDF o imprimir directo.</p>
                    <button type="button" className="lp-btn lp-btn-secondary" onClick={() => downloadQr(target, `qr-${business.usuarioEmpresa}.png`, style)}>
                        <Icon name="download"></Icon> Descargar QR en PNG
                    </button>

                    <div className="lp-panel-sizes" role="radiogroup" aria-label="Tamaño del cartel">
                        {Object.entries(SIGN_SIZES).map(([key, s]) => (
                            <button key={key} type="button" role="radio" aria-checked={size === key} className={size === key ? "lp-panel-size lp-panel-size-on" : "lp-panel-size"} onClick={() => setSize(key)}>
                                <span className={"lp-panel-size-sheet lp-panel-size-sheet-" + key} aria-hidden="true">{Array.from({ length: s.perPage }, (_, i) => <i key={i}></i>)}</span>
                                <span className="lp-panel-size-text">
                                    <strong>Cartel {s.label.toLowerCase()}</strong>
                                    <span className="lp-panel-muted">{s.detail}</span>
                                </span>
                            </button>
                        ))}
                    </div>

                    <button type="button" className="lp-btn lp-btn-primary" onClick={handlePdf} disabled={making}>
                        <Icon name="download"></Icon> {making ? "Armando el PDF..." : "Descargar cartel en PDF"}
                    </button>
                    <button type="button" className="lp-btn lp-btn-secondary" onClick={handlePrint}>
                        <Icon name="print"></Icon> Imprimir cartel
                    </button>
                </section>
            </div>
        </div>
    </div>
  )
}

export default QrPanel
